import React, { useState, useRef } from 'react';
import { View, TextInput, StyleSheet, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { Spacing, Radius } from '@constants/spacing';
import { Elevation } from '@constants/elevation';

interface SearchBarProps {
  placeholder?: string;
  onSearch: (query: string) => void;
  onVoicePress?: () => void;
  currentStock?: {
    symbol: string;
    name: string;
    hasHistory: boolean;
  } | null;
  onTickerPress?: (layout: { y: number; height: number }) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Ask anything about markets or stocks...',
  onSearch,
  onVoicePress,
  currentStock,
  onTickerPress,
}) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<View | null>(null);

  const handleSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onSearch(trimmed);
    setQuery('');
  };

  const handleClear = () => {
    setQuery('');
  };

  const handleTickerPress = () => {
    if (onTickerPress && containerRef.current) {
      containerRef.current.measure(
        (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
          onTickerPress({ y: pageY, height });
        }
      );
    }
  };

  const containerStyles = [styles.container, isFocused && styles.containerFocused];

  return (
    <View ref={containerRef} style={containerStyles}>
      <Pressable onPress={handleTickerPress} style={styles.leadingButton}>
        <Ionicons name="cash-outline" size={22} color={Colors.ai.primary} />
      </Pressable>

      <TextInput
        style={styles.input}
        placeholder={placeholder}
        placeholderTextColor={Colors.text.tertiary}
        value={query}
        onChangeText={setQuery}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onSubmitEditing={handleSearch}
        returnKeyType="search"
        autoCapitalize="none"
        autoCorrect={false}
      />

      {query.length > 0 && (
        <Pressable
          onPress={handleClear}
          style={styles.iconButton}
          accessibilityLabel="Clear search text"
        >
          <Ionicons name="close-circle" size={20} color={Colors.text.tertiary} />
        </Pressable>
      )}

      {onVoicePress && (
        <Pressable
          onPress={onVoicePress}
          style={styles.iconButton}
          accessibilityLabel="Voice search"
        >
          <Ionicons name="mic-outline" size={24} color={Colors.ai.primary} />
        </Pressable>
      )}

      <Pressable
        onPress={handleSearch}
        style={[styles.iconButton, styles.searchButton]}
        accessibilityLabel="Submit search"
      >
        <Ionicons name="search" size={24} color={Colors.ai.primary} />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 56,
    borderRadius: Radius.lg,
    backgroundColor: Colors.surface.base,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    paddingHorizontal: Spacing.md,
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.lg,
    ...Elevation.level1,
    shadowOpacity: 0.16,
    shadowRadius: 12,
  },
  containerFocused: {
    backgroundColor: Colors.surface.raised,
    borderColor: Colors.ai.accent,
    shadowColor: Colors.ai.primary,
    shadowOpacity: 0.22,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: Platform.select({ android: 8, ios: 0, default: 4 }),
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: Colors.text.primary,
    paddingVertical: 0,
  },
  iconButton: {
    paddingHorizontal: Spacing.xs,
    paddingVertical: Spacing.xs,
    marginLeft: Spacing.sm,
  },
  leadingButton: {
    paddingHorizontal: Spacing.xs,
    paddingVertical: Spacing.xs,
    marginRight: Spacing.sm,
  },
  searchButton: {
    marginLeft: Spacing.sm,
  },
});
